import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { useBookingStore } from '../store/bookingStore';
import { publicApi } from '../lib/api';
import { Lock, CheckCircle, Home, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../locales/LanguageContext';

// PayPal Client ID from env – falls back to the /config endpoint
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';

export const Step4_Payment = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const paymentDoneRef = useRef(false);
  const paypalContainerRef = useRef(null);
  const buttonsRenderedRef = useRef(false);
  const creatingReservationRef = useRef(false);

  const quoteId = useBookingStore(state => state.quoteId);
  const selectedVehicle = useBookingStore(state => state.selectedVehicle);
  const fromLabel = useBookingStore(state => state.fromLabel);
  const toLabel = useBookingStore(state => state.toLabel);
  const pickupAt = useBookingStore(state => state.pickupAt);
  const pickupTime = useBookingStore(state => state.pickupTime);
  const passengers = useBookingStore(state => state.passengers);
  const isRoundtrip = useBookingStore(state => state.isRoundtrip);
  const returnAt = useBookingStore(state => state.returnAt);
  const returnTime = useBookingStore(state => state.returnTime);
  const customerInfo = useBookingStore(state => state.customerInfo);
  const selectedAddons = useBookingStore(state => state.selectedAddons);
  const notes = useBookingStore(state => state.notes);
  const flightNumber = useBookingStore(state => state.flightNumber);
  const luggageCount = useBookingStore(state => state.luggageCount);
  const pickupAddress = useBookingStore(state => state.pickupAddress);
  const pickupExactAddress = useBookingStore(state => state.pickupExactAddress);
  const dropoffExactAddress = useBookingStore(state => state.dropoffExactAddress);
  const setReservation = useBookingStore(state => state.setReservation);
  const resetBooking = useBookingStore(state => state.resetBooking);

  // REGLA 1 & 2: reservationCreated lives in the store (survives navigation)
  const reservationCreated = useBookingStore(state => state.reservationCreated);
  const setReservationCreated = useBookingStore(state => state.setReservationCreated);
  const captchaToken = useBookingStore(state => state.captchaToken);

  useEffect(() => {
    if (paymentDoneRef.current) return;
    if (!selectedVehicle || !customerInfo.email) {
      navigate('/booking');
    }
  }, [selectedVehicle, customerInfo, navigate]);

  // Total ALWAYS comes from the backend reservation (source of truth).
  // Returns null while reservation is being created.
  const getTotal = () => {
    if (reservationCreated && reservationCreated.total != null) {
      return Number(reservationCreated.total);
    }
    return null;
  };

  // ------ 1. Create reservation ONLY if none exists for this session ------
  useEffect(() => {
    // REGLA 2: If reservation already exists in store, don't create another
    if (reservationCreated || creatingReservationRef.current || !selectedVehicle || !customerInfo.email) return;
    creatingReservationRef.current = true;

    const createRes = async () => {
      try {
        const reservationData = {
          quote_id: quoteId,
          vehicle_type_id: selectedVehicle.vehicle_type_id,
          customer: customerInfo,
          pickup_at: `${pickupAt}T${pickupTime}:00`,
          passengers: parseInt(passengers),
          is_roundtrip: isRoundtrip,
          return_at: isRoundtrip && returnAt ? `${returnAt}T${returnTime}:00` : null,
          from_label: fromLabel,
          to_label: toLabel,
          addons: selectedAddons.map(a => ({ addon_id: a.addon_id, quantity: a.quantity })),
          pickup_address: pickupAddress,
          pickup_exact_address: pickupExactAddress,
          dropoff_exact_address: dropoffExactAddress,
          notes: notes,
          flight_number: flightNumber || '',
          luggage_count: parseInt(luggageCount) || 0,
          captcha_token: captchaToken || ''
        };
        const res = await publicApi.createReservation(reservationData);
        // REGLA 1: Save to store so price is locked and survives navigation
        setReservationCreated(res.data);
      } catch (err) {
        console.error('Error creating reservation', err);
        creatingReservationRef.current = false;
        toast.error(t('step4.error_reservation'));
      }
    };
    createRes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle, customerInfo, reservationCreated]);

  // ------ 2. Load PayPal JS SDK dynamically ------
  useEffect(() => {
    // If already loaded, skip
    if (window.paypal) {
      setSdkReady(true);
      return;
    }

    const loadSdk = async () => {
      try {
        let clientId = PAYPAL_CLIENT_ID;

        // If not set in env, fetch from backend
        if (!clientId) {
          const configRes = await publicApi.getPayPalConfig();
          clientId = configRes.data.client_id;
        }

        if (!clientId) {
          setPaypalError(t('step4.error_paypal_config'));
          return;
        }

        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
        script.async = true;
        script.onload = () => setSdkReady(true);
        script.onerror = () => setPaypalError(t('step4.error_paypal_sdk'));
        document.body.appendChild(script);
      } catch (e) {
        console.error('Error loading PayPal SDK', e);
        setPaypalError(t('step4.error_paypal_load'));
      }
    };

    loadSdk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------ 3. Render PayPal Buttons once SDK + reservation are ready ------
  const renderButtons = useCallback(() => {
    if (!window.paypal || !reservationCreated || buttonsRenderedRef.current) return;
    if (!paypalContainerRef.current) return;

    buttonsRenderedRef.current = true;

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 50
      },

      // Called when the buyer clicks the PayPal button
      createOrder: async () => {
        try {
          setLoading(true);
          const res = await publicApi.createPayPalOrder(reservationCreated.reservation_id);
          return res.data.paypal_order_id; // PayPal order ID
        } catch (err) {
          console.error('Error creating PayPal order', err);
          toast.error(t('step4.error_paypal_order'));
          setLoading(false);
          throw err;
        }
      },

      // Called after the buyer approves in PayPal popup
      onApprove: async (data) => {
        try {
          setLoading(true);
          await publicApi.capturePayPalOrder(
            reservationCreated.reservation_id,
            data.orderID
          );

          setReservation(reservationCreated.reservation_id, reservationCreated.total);
          setPaymentCompleted(true);
          paymentDoneRef.current = true;
          toast.success(t('step4.success_payment'));

          navigate(`/confirmation/${reservationCreated.reservation_id}`);
          setTimeout(() => resetBooking(), 500);
        } catch (err) {
          console.error('Error capturing PayPal payment', err);
          toast.error(t('step4.error_paypal_capture'));
        } finally {
          setLoading(false);
        }
      },

      onCancel: () => {
        setLoading(false);
        toast.info(t('step4.payment_cancelled'));
      },

      onError: (err) => {
        console.error('PayPal Buttons error', err);
        setLoading(false);
        toast.error(t('step4.error_paypal_generic'));
      }
    }).render(paypalContainerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationCreated, navigate, resetBooking, setReservation]);

  useEffect(() => {
    if (sdkReady && reservationCreated) {
      renderButtons();
    }
  }, [sdkReady, reservationCreated, renderButtons]);

  if (!selectedVehicle) return null;

  return (
    <WizardLayout>
      <div data-testid="step-payment">
        {!paymentCompleted ? (
          <>
            <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-navy-950 mb-2">
              {t('step4.title')}
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              {t('step4.subtitle')}
            </p>

            <div className="space-y-4 sm:space-y-6">
              {/* Payment Summary */}
              <div className="bg-indigo-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-3 sm:mb-4">
                  <p className="text-base sm:text-lg font-semibold text-navy-900">{t('step4.total_to_pay')}</p>
                  {getTotal() != null ? (
                    <p className="text-2xl sm:text-3xl font-bold text-navy-800">
                      ${getTotal().toFixed(2)} USD
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-navy-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-slate-500">{t('step4.loading_payment')}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-slate-600">
                  {t('step4.includes_all')}
                </p>
              </div>

              {/* PayPal Buttons Section */}
              <div className="bg-white border-2 border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8">
                <div className="text-center mb-4 sm:mb-6">
                  <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-900 mb-2">
                    {t('step4.pay_with_paypal')}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm">
                    {t('step4.paypal_description')}
                  </p>
                </div>

                {/* PayPal error */}
                {paypalError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 flex items-start sm:items-center gap-2">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 shrink-0 mt-0.5 sm:mt-0" />
                    <p className="text-xs sm:text-sm text-red-700">{paypalError}</p>
                  </div>
                )}

                {/* Loading spinner while SDK / reservation load */}
                {(!sdkReady || !reservationCreated) && !paypalError && (
                  <div className="flex flex-col items-center justify-center py-6 sm:py-8 gap-2 sm:gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs sm:text-sm text-slate-500">{t('step4.loading_payment')}</p>
                  </div>
                )}

                {/* PayPal buttons container */}
                <div
                  ref={paypalContainerRef}
                  className={(!sdkReady || !reservationCreated) ? 'hidden' : ''}
                />

                {/* Processing overlay */}
                {loading && (
                  <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs sm:text-sm text-slate-600">{t('step4.processing_payment')}</span>
                  </div>
                )}

                <div className="bg-slate-50 rounded-lg p-3 sm:p-4 mt-4 sm:mt-6">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-1.5 sm:mb-2">
                    <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span>{t('step4.secure_transaction')}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-slate-500">
                    {t('step4.security_note')}
                  </p>
                </div>

                {/* Back to home button */}
                <button
                  onClick={() => {
                    resetBooking();
                    navigate('/');
                  }}
                  className="w-full h-11 sm:h-12 bg-slate-100 hover:bg-slate-200 text-slate-700
                    font-semibold text-sm sm:text-base rounded-lg transition-all mt-3 sm:mt-4
                    flex items-center justify-center gap-2"
                >
                  <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{t('step4.back_home')}</span>
                </button>
              </div>

              {/* Security Info */}
              <div className="text-center text-xs sm:text-sm text-slate-500 flex items-center justify-center gap-1.5">
                <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <p>{t('step4.ssl_protection')}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 rounded-full mb-4 sm:mb-6">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-600" />
            </div>
            <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-emerald-950 mb-2 sm:mb-3">
              {t('step4.payment_completed')}
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              {t('step4.payment_received')}
            </p>
            <div className="flex justify-center">
              <div className="w-7 h-7 sm:w-8 sm:h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        )}
      </div>
    </WizardLayout>
  );
};
